import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';

/**
 * Master Admin "Refresh All Sessions" broadcast.
 *
 * One Firestore document at `systemBroadcasts/sessionRefresh` that any signed-in
 * client subscribes to. When a Master Admin clicks the sidebar button, we stamp
 * a new `triggeredAt` server timestamp; clients react by showing a countdown
 * modal and then forcing `window.location.reload()`.
 *
 * Triggers older than the listener's session start are ignored so newly-logged-
 * in users never get a stale refresh from a previous broadcast.
 */

export const SESSION_REFRESH_DOC = 'systemBroadcasts/sessionRefresh';

export interface SessionRefreshTrigger {
  triggeredAt: Timestamp | null;
  triggeredBy?: string;
  triggeredByName?: string;
  countdownSeconds?: number;
}

export async function triggerSessionRefresh(
  uid: string,
  displayName: string,
  countdownSeconds = 30,
): Promise<void> {
  const ref = doc(db, SESSION_REFRESH_DOC);
  await setDoc(
    ref,
    {
      triggeredAt: serverTimestamp(),
      triggeredBy: uid,
      triggeredByName: displayName || '',
      countdownSeconds,
    },
    { merge: true },
  );
}

export function subscribeToSessionRefresh(
  onTrigger: (trigger: SessionRefreshTrigger) => void,
): Unsubscribe {
  const ref = doc(db, SESSION_REFRESH_DOC);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) return;
    const data = snap.data() as SessionRefreshTrigger;
    onTrigger(data);
  });
}
